import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import SimpleComboBox, { SimpleComboboxOption } from '~/components/inputs/Select/SimpleComboBox';
import { useCounterpart, useCounterparts } from '~/services/counterparts';
import { Counterpart, CounterpartDirection } from '~/types/counterparts';
import { counterpartName } from '~/utils/counterparts';
import { sortObjectsAlphabetically } from '~/utils/sort';
import styles from './CounterpartFilter.module.scss';

interface CounterpartFilterProps extends BaseSelectProps {
  direction?: CounterpartDirection;
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onChange: (value: string | null, counterpart: Counterpart | null) => void;
  value: string | null;
  placeholder?: string;
  supplierAccountId?: string;
  clientAccountId?: string;
}

function counterpartToOption(counterpart: Counterpart): SimpleComboboxOption {
  return {
    label: counterpartName(counterpart),
    value: counterpart.id,
  };
}

const CounterpartFilter = forwardRef<HTMLDivElement, CounterpartFilterProps>(
  ({ direction, onChange, value, placeholder, supplierAccountId, clientAccountId, ...props }, ref) => {
    const [searchText, setSearchText] = useState<string>('');
    const { data } = useCounterparts({
      search: searchText,
      direction,
      supplierAccountId,
      clientAccountId,
    });
    const allCounterparts = useMemo(() => data?.pages.at(0)?.counterparts ?? [], [data?.pages]);
    const { t } = useTranslation('inputs', { keyPrefix: 'counterpart_filter' });

    const { data: selectedCounterpart } = useCounterpart(searchText ? null : value);

    const options: SimpleComboboxOption[] = useMemo(() => {
      return allCounterparts.map(counterpartToOption).sort(sortObjectsAlphabetically((item) => item.label));
    }, [allCounterparts]);

    const handleChange = useCallback(
      (selectedValue: string | null) => {
        const selectedAccount = allCounterparts.find((account) => account.id === selectedValue) ?? null;
        setSearchText(selectedAccount ? counterpartName(selectedAccount) : '');
        onChange(selectedValue, selectedAccount);
      },
      [allCounterparts, onChange],
    );

    const handleClear = useCallback(() => {
      setSearchText('');
      onChange(null, null);
    }, [onChange]);

    useEffect(() => {
      if (selectedCounterpart && !searchText) {
        setSearchText(counterpartName(selectedCounterpart));
      }
    }, [searchText, selectedCounterpart]);

    useEffect(() => {
      if (value === null) {
        setSearchText('');
      }
    }, [value]);

    return (
      <SimpleComboBox
        {...props}
        ref={ref}
        onChange={handleChange}
        inputValue={searchText}
        onInputChange={(value) => {
          setSearchText(value);
          if (!value) onChange(null, null);
        }}
        options={options}
        value={value}
        className={clsx(props.className, styles.select)}
        placeholder={placeholder || t('placeholder')}
        hasClearButton
        onClear={value !== null ? handleClear : undefined}
        allowsEmptyCollection
      />
    );
  },
);

CounterpartFilter.displayName = 'CounterpartFilter';

export default CounterpartFilter;
